import * as React from "react"
import { CheckIcon, MinusIcon } from "lucide-react"

import { cn } from "@/lib/utils"

// Not built on Radix Checkbox: its setState-as-ref loops ("Maximum update depth exceeded") when the indicator
// detaches on toggle, on any React version. A plain button sidesteps it.

type CheckedState = boolean | "indeterminate"

interface CheckboxProps
  extends Omit<React.ComponentPropsWithoutRef<"button">, "type" | "onChange"> {
  checked?: CheckedState
  onCheckedChange?: (checked: boolean) => void
}

function Checkbox({
  className,
  checked = false,
  onCheckedChange,
  onClick,
  ...props
}: CheckboxProps) {
  const isChecked = checked === true
  const isIndeterminate = checked === "indeterminate"

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isIndeterminate ? "mixed" : isChecked}
      data-slot="checkbox"
      data-state={isChecked ? "checked" : isIndeterminate ? "indeterminate" : "unchecked"}
      onClick={(e) => {
        onClick?.(e)
        if (e.defaultPrevented) return
        onCheckedChange?.(!isChecked)
      }}
      className={cn(
        "peer inline-grid size-4 shrink-0 place-content-center rounded-[4px] border border-input text-primary-foreground shadow-xs transition-shadow outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary dark:bg-input/30 dark:aria-invalid:ring-destructive/40 dark:data-[state=checked]:bg-primary dark:data-[state=indeterminate]:bg-primary",
        className
      )}
      {...props}
    >
      {isChecked ? (
        <CheckIcon className="size-3.5" />
      ) : isIndeterminate ? (
        <MinusIcon className="size-3.5" />
      ) : null}
    </button>
  )
}

export { Checkbox }
